import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { ImpactDashboardDays, ImpactDashboardResponse } from "@excalidraw-ms/shared";
import { BackendError, getImpactDashboard } from "../api/backendClient";
import { useAuth } from "../auth/msalClient";
import ImpactDashboardSkeleton from "../components/impact/ImpactDashboardSkeleton";
import ImpactInfoTooltip from "../components/impact/ImpactInfoTooltip";
import ImpactMetricCard from "../components/impact/ImpactMetricCard";
import ImpactTrendChart from "../components/impact/ImpactTrendChart";
import HomeButton from "../components/HomeButton";
import UserProfileMenu from "../components/UserProfileMenu";
import { useThemePreference } from "../hooks/useThemePreference";
import "../styles/impact-dashboard.css";

const PERIODS: ImpactDashboardDays[] = [30, 90, 365];
const METRIC_DEFINITIONS = {
  visitors:
    "Distinct entitled people observed accessing Excalidraw. Every active user is included as a visitor; opening additional tabs or entry points does not increase the distinct count.",
  dau: "Distinct visitors who edited a scene, created, loaded, or saved a cloud drawing, connected to collaboration, or successfully used a non-health AI tool on this complete UTC day.",
  wau: "Distinct people active during the trailing 7 complete UTC days. This is an exact distinct count, not a sum of daily users.",
  mau: "Distinct people active during the trailing 30 complete UTC days. This is an exact distinct count, not a sum of daily users.",
  activeCloudDrawings:
    "Drawings currently active in the managed cloud catalog at snapshot time. Deleted drawings and browser-only scenes are excluded.",
  cloudStorage:
    "Total recorded scene bytes for active managed cloud drawings at snapshot time. Browser-only scenes and read-only share snapshots are excluded.",
  drawingsStarted:
    "New browser scenes first edited plus drawings created through AI during the selected period. Includes local and cloud drawings; saving a browser scene is not required.",
  lifetimeDrawingsStarted:
    "Drawings started from available recorded activity since general availability, including local and AI-created drawings. Initial launch-period data is incomplete. This is observed activity, not an exact inventory.",
  appUsageSessions:
    "Browser engagement grouped by person across drawings and tabs. Writing, reading, sharing, drawing management, feedback, and import/export interactions count even if cancelled or unsuccessful. More than 30 minutes without observed activity starts another session. Background activity and MCP-only calls are excluded.",
  cloudDrawingsCreated:
    "Drawings successfully committed to managed cloud storage during the selected period. Retries of the same create count once.",
  collaborationSessions:
    "Five-minute room activity windows with at least two distinct authenticated participants. One-person rooms do not count.",
  aiAssistedSessions:
    "Successful AI tool activity grouped by person, client application, and drawing. A new session begins after more than 30 inactive minutes for that combination.",
  metricTrend:
    "Daily values for the selected headline metric. Cloud footprint values are point-in-time inventory snapshots; activity values count events or sessions assigned to each complete UTC day.",
} as const;

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let result = value;
  let unit = -1;
  do {
    result /= 1024;
    unit += 1;
  } while (result >= 1024 && unit < units.length - 1);
  return `${result.toFixed(result >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatPeriod(days: ImpactDashboardDays): string {
  return days === 365 ? "the last year" : `the last ${days} days`;
}

export default function ImpactDashboardPage() {
  const navigate = useNavigate();
  const {
    account,
    loading: authLoading,
    backendAccessLoading,
    hasBackendAccess,
    canViewImpactDashboard,
    getToken,
    signIn,
    signOut,
  } = useAuth();
  const { preference: themePreference, setPreference: setThemePreference } = useThemePreference({
    accountId: account?.homeAccountId,
    getToken,
    syncEnabled: hasBackendAccess,
  });
  const [days, setDays] = useState<ImpactDashboardDays>(90);
  const [data, setData] = useState<ImpactDashboardResponse | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable" | "denied">("loading");

  useEffect(() => {
    if (!account || authLoading || backendAccessLoading) return;
    const controller = new AbortController();
    setState("loading");
    void getToken()
      .then((token) => getImpactDashboard(token, days, controller.signal))
      .then((snapshot) => {
        setData(snapshot);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState(error instanceof BackendError && error.status === 403 ? "denied" : "unavailable");
      });
    return () => controller.abort();
  }, [account, authLoading, backendAccessLoading, days, getToken]);

  if (authLoading || backendAccessLoading) {
    return (
      <main className="impact-page">
        <ImpactDashboardSkeleton />
      </main>
    );
  }

  if (!account) {
    return (
      <main className="impact-page impact-page--centered">
        <section className="impact-state-card">
          <h1>Sign in required</h1>
          <p>Sign in with an authorized Excalidraw-MS administrator account.</p>
          <button className="impact-button impact-button--primary" onClick={() => void signIn()}>
            Sign in
          </button>
        </section>
      </main>
    );
  }

  if (state === "denied") {
    return (
      <main className="impact-page impact-page--centered">
        <div className="impact-dashboard-actions">
          <HomeButton onClick={() => navigate("/")} />
          <UserProfileMenu
            account={account}
            onSignOut={signOut}
            themePreference={themePreference}
            onThemePreferenceChange={setThemePreference}
          />
        </div>
        <section className="impact-state-card">
          <span className="impact-state-card__code">403</span>
          <h1>Dashboard access is restricted</h1>
          <p>The Excalidraw.Admin backend app role is required to view business-impact data.</p>
          <button className="impact-button" onClick={() => navigate("/")}>
            Return to editor
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="impact-page" aria-busy={state === "loading"}>
      <div className="impact-dashboard-actions">
        <HomeButton onClick={() => navigate("/")} />
        <UserProfileMenu
          account={account}
          onSignOut={signOut}
          themePreference={themePreference}
          onThemePreferenceChange={setThemePreference}
          canViewImpactDashboard={canViewImpactDashboard}
          onOpenImpactDashboard={() => navigate("/dashboard")}
        />
      </div>
      <header className="impact-header">
        <div>
          <h1>Business impact</h1>
          <p className="impact-subtitle">
            Adoption, creation, collaboration, and AI-assisted work across Excalidraw-MS.
          </p>
        </div>
        {state !== "unavailable" && data && (
          <p className="impact-as-of">As of {data.completeThrough}</p>
        )}
      </header>

      {state === "loading" && !data && <ImpactDashboardSkeleton />}
      {state === "unavailable" && (
        <section className="impact-state-card impact-state-card--inline">
          <h2>Impact data is temporarily unavailable</h2>
          <p>
            The latest aggregate has not been published. Drawing and collaboration services are
            unaffected.
          </p>
        </section>
      )}
      {state !== "unavailable" && data && (
        <>
          {data.freshness === "stale" && (
            <div className="impact-stale-banner" role="status">
              Aggregation is delayed. Showing the latest complete snapshot from{" "}
              {data.completeThrough}.
            </div>
          )}
          {data.selectedPeriodCoverage.partial && (
            <div className="impact-stale-banner" role="status">
              Partial history: {formatNumber(data.selectedPeriodCoverage.availableDays)} of{" "}
              {formatNumber(data.selectedPeriodCoverage.requestedDays)} requested days are
              available. Selected-period totals and trends include only available complete UTC days.
            </div>
          )}

          <section aria-labelledby="visitors-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Access · daily and rolling windows</p>
                <h2 id="visitors-heading">Visitors</h2>
              </div>
            </div>
            <div className="impact-metric-grid impact-metric-grid--three">
              <ImpactMetricCard
                label="Daily visitors"
                value={formatNumber(data.windows.dailyVisitors)}
                description={`Accessed on ${data.completeThrough}`}
                definition={METRIC_DEFINITIONS.visitors}
              />
              <ImpactMetricCard
                label="Weekly visitors"
                value={formatNumber(data.windows.weeklyVisitors)}
                description="Distinct visitors · trailing 7 days"
                definition={METRIC_DEFINITIONS.visitors}
              />
              <ImpactMetricCard
                label="Monthly visitors"
                value={formatNumber(data.windows.monthlyVisitors)}
                description="Distinct visitors · trailing 30 days"
                definition={METRIC_DEFINITIONS.visitors}
              />
            </div>
          </section>

          <section aria-labelledby="active-users-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Engagement · daily and rolling windows</p>
                <h2 id="active-users-heading">Active users</h2>
              </div>
            </div>
            <div className="impact-metric-grid impact-metric-grid--three">
              <ImpactMetricCard
                label="Daily active users"
                value={formatNumber(data.windows.dau)}
                description={`Active on ${data.completeThrough}`}
                definition={METRIC_DEFINITIONS.dau}
              />
              <ImpactMetricCard
                label="Weekly active users"
                value={formatNumber(data.windows.wau)}
                description="Distinct users · trailing 7 days"
                definition={METRIC_DEFINITIONS.wau}
              />
              <ImpactMetricCard
                label="Monthly active users"
                value={formatNumber(data.windows.mau)}
                description="Distinct users · trailing 30 days"
                definition={METRIC_DEFINITIONS.mau}
              />
            </div>
          </section>

          <section aria-labelledby="cloud-footprint-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Current totals</p>
                <h2 id="cloud-footprint-heading">Cloud footprint</h2>
              </div>
            </div>
            <div className="impact-metric-grid impact-metric-grid--two">
              <ImpactMetricCard
                label="Active cloud drawings"
                value={formatNumber(data.totals.activeCloudDrawings)}
                description="Current managed inventory"
                definition={METRIC_DEFINITIONS.activeCloudDrawings}
              />
              <ImpactMetricCard
                label="Cloud storage used"
                value={formatBytes(data.totals.activeCloudStorageBytes)}
                description="Current drawing scene inventory"
                definition={METRIC_DEFINITIONS.cloudStorage}
              />
            </div>
          </section>

          <section aria-labelledby="lifetime-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Independent of the selected period</p>
                <h2 id="lifetime-heading">Cumulative creation</h2>
              </div>
              {data.lifetime && data.lifetime.processedThrough !== data.completeThrough && (
                <span>As of {data.lifetime.processedThrough}</span>
              )}
            </div>
            <ImpactMetricCard
              label="Total drawings started"
              value={data.lifetime ? formatNumber(data.lifetime.drawingsStarted) : "Not available"}
              description={
                data.lifetime
                  ? data.lifetime.missingDays > 0
                    ? `Since GA · August 10, 2026 · ${formatNumber(data.lifetime.missingDays)} missing ${data.lifetime.missingDays === 1 ? "day" : "days"}`
                    : "Since GA · August 10, 2026"
                  : "Lifetime collection has not been initialized or is unavailable"
              }
              definition={METRIC_DEFINITIONS.lifetimeDrawingsStarted}
            />
            {data.lifetime && data.lifetime.processedThrough < data.completeThrough && (
              <p role="status">The cumulative total is behind the latest daily snapshot.</p>
            )}
          </section>

          <section aria-labelledby="activity-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Adoption and engagement</p>
                <h2 id="activity-heading">
                  Activity during {formatPeriod(data.selectedPeriodCoverage.requestedDays)}
                </h2>
              </div>
              <div className="impact-period-picker" role="group" aria-label="Reporting period">
                {PERIODS.map((period) => (
                  <button
                    key={period}
                    className={period === days ? "is-selected" : ""}
                    aria-disabled={state === "loading"}
                    onClick={() => {
                      if (state !== "loading") setDays(period);
                    }}
                    aria-pressed={period === days}
                  >
                    {period === 365 ? "1 year" : `${period} days`}
                  </button>
                ))}
              </div>
            </div>
            <div className="impact-metric-grid impact-metric-grid--two">
              <ImpactMetricCard
                label="Drawings started"
                value={formatNumber(data.selectedPeriod.drawingsStarted)}
                description="Web app + AI · local and cloud"
                definition={METRIC_DEFINITIONS.drawingsStarted}
              />
              <ImpactMetricCard
                label="Cloud drawings created"
                value={formatNumber(data.selectedPeriod.cloudDrawingsCreated)}
                description="Committed to managed cloud storage"
                definition={METRIC_DEFINITIONS.cloudDrawingsCreated}
              />
              <ImpactMetricCard
                label="App usage sessions"
                value={
                  data.selectedPeriod.appUsageSessions == null
                    ? "Not available"
                    : formatNumber(data.selectedPeriod.appUsageSessions)
                }
                description={
                  data.appUsageCoverage?.partial
                    ? `Partial coverage · ${formatNumber(data.appUsageCoverage.availableDays)} ${data.appUsageCoverage.availableDays === 1 ? "day" : "days"} · observed engagement`
                    : "Per person · 30-minute activity windows · observed engagement"
                }
                definition={METRIC_DEFINITIONS.appUsageSessions}
              />
              <ImpactMetricCard
                label="Collaboration sessions"
                value={formatNumber(data.selectedPeriod.collaborationSessions)}
                description={`${formatNumber(data.selectedPeriod.collaborationParticipants)} participants`}
                definition={METRIC_DEFINITIONS.collaborationSessions}
              />
              <ImpactMetricCard
                label="AI-assisted sessions"
                value={formatNumber(data.selectedPeriod.aiAssistedSessions)}
                description={`Per user + client + drawing · 30-minute windows · ${formatNumber(data.selectedPeriod.aiToolCalls)} successful tool calls`}
                definition={METRIC_DEFINITIONS.aiAssistedSessions}
              />
            </div>
          </section>

          <section aria-labelledby="trend-heading">
            <div className="impact-section-heading">
              <div>
                <p className="impact-eyebrow">Trend</p>
                <div className="impact-heading-with-info">
                  <h2 id="trend-heading">Metric trends</h2>
                  <ImpactInfoTooltip
                    label="Metric trends"
                    definition={METRIC_DEFINITIONS.metricTrend}
                  />
                </div>
              </div>
            </div>
            <div className="impact-panel">
              <ImpactTrendChart points={data.trend} />
            </div>
          </section>

          <footer className="impact-footer">
            Generated {new Date(data.generatedAt).toLocaleString()} · Definitions v
            {data.definitionsVersion}
          </footer>
        </>
      )}
    </main>
  );
}
